import { useCallback, useEffect, useLayoutEffect, useRef, type ReactNode } from "react";
import { useMediaQuery } from "@mantine/hooks";
import { useAppearance } from "../../theme/AppearanceContext";
import { CardList } from "./CardList";
import { CompactList } from "./CompactList";
import type { ResponsiveTableProps } from "./types";
import "./responsiveTable.css";

export type { MobileField, MobileSort, MobileView, ResponsiveTableProps } from "./types";

const MOBILE_QUERY = "(max-width: 48em)";

/**
 * Desktop (≥ sm): renders `children` exactly as given.
 * Phone: per the appearance preference (or `forceMode`) — the same table with sticky first column(s),
 * a compact expandable list, or cards built from `mobile`.
 */
export function ResponsiveTable<T>({ mobile, stickyColumns = 1, forceMode, stagger = true, children }: ResponsiveTableProps<T>) {
  const { tableMode } = useAppearance();
  const isMobile = useMediaQuery(MOBILE_QUERY, false, { getInitialValueInEffect: false });
  const mode = forceMode ?? tableMode;

  if (!isMobile) return <>{children}</>;
  if (mode === "sticky") return <StickyTable columns={stickyColumns}>{children}</StickyTable>;
  if (mode === "cards") return <CardList view={mobile} stagger={stagger} />;
  return <CompactList view={mobile} stagger={stagger} />;
}

function StickyTable({ columns, children }: { columns: 1 | 2; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);

  // Copies each row's computed background onto a variable that the opaque sticky cells layer over the
  // theme surface (striping, rowBg tints), and measures column 1 to offset column 2.
  // All reads first, then only the changed writes, so a sync costs a single style recalc.
  const sync = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const rows = Array.from(el.querySelectorAll<HTMLTableRowElement>("tr"));
    const tints = rows.map((tr) => {
      const bg = getComputedStyle(tr).backgroundColor;
      return bg && bg !== "transparent" && bg !== "rgba(0, 0, 0, 0)" ? bg : "";
    });
    const first = columns === 2 ? el.querySelector<HTMLTableCellElement>("tr > :first-child:not([colspan])") : null;
    const w1 = columns === 2 ? `${first?.offsetWidth ?? 0}px` : "";
    rows.forEach((tr, i) => {
      if (tr.style.getPropertyValue("--app-sticky-tint") === tints[i]) return;
      if (tints[i]) tr.style.setProperty("--app-sticky-tint", tints[i]);
      else tr.style.removeProperty("--app-sticky-tint");
    });
    if (columns === 2 && el.style.getPropertyValue("--app-sticky-w1") !== w1) el.style.setProperty("--app-sticky-w1", w1);
  }, [columns]);

  // Re-sync after renders (rows/tints may have changed); cheap when nothing did.
  useLayoutEffect(sync);

  // Column widths can change without a re-render (fonts, viewport): one observer per mount/columns.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let first = true;
    const ro = new ResizeObserver(() => {
      if (first) first = false; // initial callback: the layout effect already synced
      else sync();
    });
    ro.observe(el);
    const table = el.querySelector("table");
    if (table) ro.observe(table);
    return () => ro.disconnect();
  }, [sync]);

  return (
    <div ref={ref} className="app-table-sticky" data-sticky={columns}>
      {children}
    </div>
  );
}
